/**
 * Component: Read More (Expandable Text)
 * Purpose: Handles the expand/collapse logic for text blocks with resize awareness.
 * Reference: FoxTheme Framework - Section 3 (JS Architecture)
 */

class ReadMoreComponent extends HTMLElement {
  constructor() {
    super();
    // Selectors: Prioritize data-attributes over classes.
    this.wrapper = this.querySelector("[data-read-more-wrapper]");
    this.content = this.querySelector("[data-read-more-content]");
    this.trigger = this.querySelector("[data-read-more-trigger]");

    // Parse config
    this.maxHeight = parseInt(this.dataset.maxHeight) || 150;
    this.expandLabel = this.dataset.labelExpand;
    this.collapseLabel = this.dataset.labelCollapse;

    this.isExpanded = false;
    this.resizeObserver = null;

    if (this.content && this.wrapper && this.trigger) {
      this.init();
    }
  }

  init() {
    this.trigger.addEventListener("click", this.toggle.bind(this));
    this.calculateState();

    // Use ResizeObserver to handle responsive text wrapping changes
    this.resizeObserver = new ResizeObserver(() => {
      // requestAnimationFrame ensures we don't trigger "Loop limit exceeded" errors
      requestAnimationFrame(() => {
        if (this.isConnected) this.calculateState();
      });
    });
    this.resizeObserver.observe(this.content);
  }

  disconnectedCallback() {
    if (this.resizeObserver) this.resizeObserver.disconnect();
  }

  calculateState() {
    // If manually expanded, keep it open regardless of resize
    if (this.isExpanded) {
      this.wrapper.style.maxHeight = `${this.content.scrollHeight}px`;
      return;
    }

    // Logic: Only show button/gradient if content exceeds max-height
    if (this.content.scrollHeight <= this.maxHeight) {
      this.setAttribute("data-state", "hidden-trigger");
      this.wrapper.style.maxHeight = "none";
      this.trigger.setAttribute("hidden", "");
    } else {
      this.setAttribute("data-state", "collapsed");
      this.wrapper.style.maxHeight = `${this.maxHeight}px`;
      this.trigger.removeAttribute("hidden");
    }
  }

  toggle() {
    this.isExpanded = !this.isExpanded;

    if (this.isExpanded) {
      // Expand
      this.setAttribute("data-state", "expanded");
      this.wrapper.style.maxHeight = `${this.content.scrollHeight}px`;
      this.trigger.textContent = this.collapseLabel;
      this.trigger.setAttribute("aria-expanded", "true");
    } else {
      // Collapse
      this.setAttribute("data-state", "collapsed");
      this.wrapper.style.maxHeight = `${this.maxHeight}px`;
      this.trigger.textContent = this.expandLabel;
      this.trigger.setAttribute("aria-expanded", "false");
    }
  }
}

// Scope: No global window pollution.
if (!customElements.get("read-more-component")) {
  customElements.define("read-more-component", ReadMoreComponent);
}
